import { useCallback, useEffect, useMemo, useState } from "react";
import type { MemberRow } from "../../../api/types";
import { apiPut } from "../../../api/http";
import { useAdminData } from "../AdminDataContext";
import { memberLevelStore } from "../utils/memberLevelStore";
import MemberDetailModal from "./MemberDetailModal";
import { reportError, showUserNotice } from "../../../api/errors";
import { wxBindBadge } from "../utils/wxBindDisplay";
import {
  MEMBER_GROUP_FILTER_NONE,
  enabledMemberGroupDefinitions,
  fetchAllMemberGroupDefinitionsForTenant,
  memberGroupNamesDisplay,
  memberGroupsFromMemberRow,
  type MemberGroupDefinition,
} from "../../../api/memberGroupDefinitions";
import {
  fetchMemberBlacklist,
  removeMemberBlacklistEntry,
  type MemberBlacklistRow,
} from "../../../api/memberBlacklist";

const activityOptions = ["全部", "活跃", "未活跃"] as const;

type ListTab = "all" | "approved" | "expired" | "blacklist";

export interface LevelChangeLog {
  id: string;
  date: string;
  time: string;
  oldLevel: string;
  newLevel: string;
  operator: string;
  note?: string;
}

function todayYmdLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 与后端 WhereMemberPortalActiveInForceMembers 一致 */
function isInForceMember(m: MemberRow, today: string): boolean {
  const st = String(m.status || "").toLowerCase();
  if (st !== "active") return false;
  const until = (m.membershipValidUntil || "").trim();
  if (!until) return true;
  return until >= today;
}

/** 与后端 WhereMemberPortalExpiredMembers 一致 */
function isExpiredMember(m: MemberRow, today: string): boolean {
  const st = String(m.status || "").toLowerCase();
  if (st === "expired") return true;
  if (st === "active") {
    const until = (m.membershipValidUntil || "").trim();
    return until.length > 0 && until < today;
  }
  return false;
}

function memberMatchesGroupFilter(m: MemberRow, groupKey: string | null): boolean {
  if (groupKey == null) return true;
  const groups = memberGroupsFromMemberRow(m);
  if (groupKey === MEMBER_GROUP_FILTER_NONE) return groups.length === 0;
  return groups.some((g) => String(g.memberGroupKey || "").trim() === groupKey);
}

export default function MemberList() {
  const { members, tenantLegacyId, reload } = useAdminData();
  const [listTab, setListTab] = useState<ListTab>("all");
  const [levelFilter, setLevelFilter] = useState("全部");
  const [activityFilter, setActivityFilter] = useState<(typeof activityOptions)[number]>("全部");
  const [search, setSearch] = useState("");
  const [groupFilterKey, setGroupFilterKey] = useState<string | null>(null);
  const [groupDefs, setGroupDefs] = useState<MemberGroupDefinition[]>([]);
  const [viewMember, setViewMember] = useState<MemberRow | null>(null);

  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editLevelValue, setEditLevelValue] = useState("");
  const [editLevelNote, setEditLevelNote] = useState("");
  const [levelSaving, setLevelSaving] = useState(false);

  const [blacklistItems, setBlacklistItems] = useState<MemberBlacklistRow[]>([]);
  const [blacklistTotal, setBlacklistTotal] = useState(0);
  const [blacklistLoading, setBlacklistLoading] = useState(false);
  const [blacklistRemoveBusyId, setBlacklistRemoveBusyId] = useState<string | null>(null);

  const today = useMemo(() => todayYmdLocal(), []);
  const levelNames = memberLevelStore.getNames();
  const levels = memberLevelStore.getAll();
  const levelOptions = ["全部", ...levelNames];

  const loadGroupDefs = useCallback(async () => {
    try {
      const defs = await fetchAllMemberGroupDefinitionsForTenant(tenantLegacyId);
      setGroupDefs(enabledMemberGroupDefinitions(defs));
    } catch {
      setGroupDefs([]);
    }
  }, [tenantLegacyId]);

  const loadBlacklist = useCallback(async () => {
    setBlacklistLoading(true);
    try {
      const res = await fetchMemberBlacklist(tenantLegacyId, 1, 100);
      setBlacklistItems(Array.isArray(res.items) ? res.items : []);
      setBlacklistTotal(Number(res.total) || 0);
    } catch (e) {
      setBlacklistItems([]);
      setBlacklistTotal(0);
      reportError(e, "加载黑名单失败");
    } finally {
      setBlacklistLoading(false);
    }
  }, [tenantLegacyId]);

  useEffect(() => {
    void loadGroupDefs();
  }, [loadGroupDefs]);

  useEffect(() => {
    void loadBlacklist();
  }, [loadBlacklist]);

  const approvedMembers = useMemo(
    () => members.filter((m) => isInForceMember(m, today)),
    [members, today],
  );
  const expiredMembers = useMemo(
    () => members.filter((m) => isExpiredMember(m, today)),
    [members, today],
  );

  const tabMembers = useMemo(() => {
    if (listTab === "expired") return expiredMembers;
    if (listTab === "blacklist") return [];
    // 全部 / 已通过：与移动端一致，均为在册有效会员
    return approvedMembers;
  }, [listTab, approvedMembers, expiredMembers]);

  const getMemberLevel = (m: MemberRow) => m.level;

  const openEditLevel = (m: MemberRow) => {
    setEditLevelValue(getMemberLevel(m));
    setEditLevelNote("");
    setEditingMemberId(m.id);
  };

  const confirmEditLevel = async () => {
    if (!editingMemberId || !editLevelValue || levelSaving) return;
    const member = members.find((m) => m.id === editingMemberId);
    if (!member) return;
    const oldLevel = member.level;
    if (editLevelValue === oldLevel) return;
    setLevelSaving(true);
    try {
      await apiPut<MemberRow>(`/api/tenants/${tenantLegacyId}/members/${encodeURIComponent(member.id)}`, {
        levelName: editLevelValue,
        levelChangeNote: editLevelNote.trim() || null,
      });
      setEditingMemberId(null);
      await reload();
    } catch (e) {
      reportError(e, "修改失败");
    } finally {
      setLevelSaving(false);
    }
  };

  const editingMember = members.find((m) => m.id === editingMemberId) ?? null;
  const viewMemberLive = viewMember ? members.find((m) => m.id === viewMember.id) ?? viewMember : null;

  const filtered = useMemo(() => {
    return tabMembers.filter((m) => {
      const currentLevel = getMemberLevel(m);
      const matchLevel = levelFilter === "全部" || currentLevel === levelFilter;
      const matchActivity =
        activityFilter === "全部" ||
        (activityFilter === "活跃" && m.status === "active") ||
        (activityFilter === "未活跃" && m.status === "inactive");
      const q = search.trim();
      const matchSearch =
        !q ||
        m.name.includes(q) ||
        m.realName.includes(q) ||
        m.email.includes(q) ||
        m.phone.includes(q) ||
        (m.memberNo || "").includes(q);
      const matchGroup = listTab === "expired" ? true : memberMatchesGroupFilter(m, groupFilterKey);
      return matchLevel && matchActivity && matchSearch && matchGroup;
    });
  }, [tabMembers, levelFilter, activityFilter, search, groupFilterKey, listTab]);

  const showGroupSidebar = listTab === "all" || listTab === "approved";

  const removeBlacklist = async (row: MemberBlacklistRow) => {
    const leg = String(row.legacyId || "").trim();
    if (!leg || blacklistRemoveBusyId) return;
    if (!window.confirm(`确认解除「${row.name || "该人员"}」的黑名单限制？`)) return;
    setBlacklistRemoveBusyId(leg);
    try {
      await removeMemberBlacklistEntry(tenantLegacyId, leg);
      showUserNotice("已解除黑名单");
      await loadBlacklist();
    } catch (e) {
      reportError(e, "解除失败");
    } finally {
      setBlacklistRemoveBusyId(null);
    }
  };

  const tabs: { key: ListTab; label: string; badge?: number }[] = [
    { key: "all", label: "全部" },
    { key: "approved", label: "已通过", badge: approvedMembers.length },
    { key: "expired", label: "已过期", badge: expiredMembers.length },
    { key: "blacklist", label: "黑名单", badge: blacklistTotal },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-50">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="font-bold text-gray-900">会员列表</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              已通过 {approvedMembers.length} · 已过期 {expiredMembers.length}
              {listTab !== "blacklist" ? ` · 当前 ${filtered.length} 人` : ` · 黑名单 ${blacklistTotal} 人`}
            </p>
          </div>
          {listTab !== "blacklist" && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="搜索姓名/手机/编号..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-red-400 w-48"
                />
              </div>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="text-sm border border-gray-200 rounded-xl px-3 py-1.5 focus:outline-none focus:border-red-400 cursor-pointer"
              >
                {levelOptions.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
              <select
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value as (typeof activityOptions)[number])}
                className="text-sm border border-gray-200 rounded-xl px-3 py-1.5 focus:outline-none focus:border-red-400 cursor-pointer"
              >
                {activityOptions.map((s) => (
                  <option key={s} value={s}>
                    {s === "全部" ? "活跃度：全部" : s}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-1 mt-4 border-b border-gray-100 -mb-px">
          {tabs.map((t) => {
            const on = listTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setListTab(t.key);
                  if (t.key === "expired" || t.key === "blacklist") setGroupFilterKey(null);
                }}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  on ? "border-red-500 text-red-600" : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
              >
                {t.label}
                {t.badge != null && t.badge > 0 && (t.key === "expired" || t.key === "blacklist") ? (
                  <span className="ml-1.5 text-[11px] font-medium text-orange-600">{t.badge}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {listTab === "blacklist" ? (
        <div className="overflow-x-auto">
          {blacklistLoading ? (
            <div className="py-12 text-center text-sm text-gray-400">加载黑名单…</div>
          ) : blacklistItems.length === 0 ? (
            <div className="py-12 text-center">
              <i className="ri-shield-check-line text-3xl text-gray-200 mb-2 block"></i>
              <p className="text-sm text-gray-400">暂无黑名单记录</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  {["姓名", "手机", "证件号", "来源", "原因", "录入时间", "操作"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {blacklistItems.map((row) => {
                  const busy = blacklistRemoveBusyId === row.legacyId;
                  return (
                    <tr key={row.legacyId} className="hover:bg-gray-50/50">
                      <td className="px-5 py-3.5 font-medium text-gray-900">{row.name || "—"}</td>
                      <td className="px-5 py-3.5 text-gray-600">{row.phone || "—"}</td>
                      <td className="px-5 py-3.5 font-mono text-xs text-gray-500">{row.idNumber || "—"}</td>
                      <td className="px-5 py-3.5 text-xs text-gray-600">{row.sourceLabel || row.source || "—"}</td>
                      <td className="px-5 py-3.5 text-xs text-gray-500 max-w-xs truncate" title={row.reason}>
                        {row.reason || "—"}
                      </td>
                      <td className="px-5 py-3.5 text-xs text-gray-400 whitespace-nowrap">
                        {row.createdAtUtc ? new Date(row.createdAtUtc).toLocaleString("zh-CN") : "—"}
                      </td>
                      <td className="px-5 py-3.5">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void removeBlacklist(row)}
                          className="px-3 py-1.5 text-xs rounded-xl border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 cursor-pointer"
                        >
                          {busy ? "处理中…" : "解除"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="flex min-h-[420px]">
          {showGroupSidebar && (
            <aside className="w-40 flex-shrink-0 border-r border-gray-100 bg-gray-50/80 overflow-y-auto">
              <p className="px-3 py-2.5 text-[11px] font-semibold text-gray-400 tracking-wide">会员分组</p>
              <button
                type="button"
                onClick={() => setGroupFilterKey(null)}
                className={`w-full text-left px-3 py-2.5 text-sm border-b border-gray-100 cursor-pointer transition-colors ${
                  groupFilterKey === null ? "bg-red-500 text-white font-semibold" : "text-gray-600 hover:bg-white"
                }`}
              >
                全部
              </button>
              <button
                type="button"
                onClick={() => setGroupFilterKey(MEMBER_GROUP_FILTER_NONE)}
                className={`w-full text-left px-3 py-2.5 text-sm border-b border-gray-100 cursor-pointer transition-colors ${
                  groupFilterKey === MEMBER_GROUP_FILTER_NONE
                    ? "bg-red-500 text-white font-semibold"
                    : "text-gray-600 hover:bg-white"
                }`}
              >
                未分组
              </button>
              {groupDefs.map((g) => {
                const active = groupFilterKey === g.id;
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setGroupFilterKey(g.id)}
                    className={`w-full text-left px-3 py-2.5 text-sm border-b border-gray-100 cursor-pointer transition-colors truncate ${
                      active ? "bg-red-500 text-white font-semibold" : "text-gray-600 hover:bg-white"
                    }`}
                    title={g.name}
                  >
                    {g.name}
                  </button>
                );
              })}
              {groupDefs.length === 0 && (
                <p className="px-3 py-4 text-[11px] text-gray-400 leading-relaxed">
                  暂无分组，请先在系统配置中维护会员分组字典。
                </p>
              )}
            </aside>
          )}

          <div className="flex-1 min-w-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  {["昵称 / 姓名", "会员编号", "分组", "联系方式", "微信绑定", "所在地", "会员等级", "积分", "加入时间", "状态", "操作"].map(
                    (h) => (
                      <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((m) => {
                  const currentLevel = getMemberLevel(m);
                  const groupLabel = memberGroupNamesDisplay(m) || "—";
                  const expired = isExpiredMember(m, today);
                  return (
                    <tr key={m.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-user-line text-red-500 text-sm"></i>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{m.name}</p>
                            <p className="text-xs text-gray-400">{m.realName}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="text-xs font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded-lg whitespace-nowrap">
                          {m.memberNo ?? "—"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="text-xs text-teal-800 bg-teal-50 px-2 py-0.5 rounded-lg max-w-[140px] inline-block truncate" title={groupLabel}>
                          {groupLabel}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-gray-700">{m.phone}</p>
                        <p className="text-xs text-gray-400">{m.email}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        {(() => {
                          const b = wxBindBadge(m.wxOpenId);
                          return b.bound ? (
                            <span
                              title={b.title}
                              className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full"
                            >
                              <i className="ri-wechat-fill" style={{ color: "#07C160" }} />
                              已绑定
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">未绑定</span>
                          );
                        })()}
                      </td>
                      <td className="px-5 py-3.5 text-gray-500">{m.city}</td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${memberLevelStore.getClassName(currentLevel)}`}>
                          {currentLevel}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 font-semibold text-red-600">{m.points.toLocaleString()}</td>
                      <td className="px-5 py-3.5 text-gray-500">{m.joinDate}</td>
                      <td className="px-5 py-3.5">
                        {expired ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-400"></span>
                            已过期
                          </span>
                        ) : (
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                              m.status === "active" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${m.status === "active" ? "bg-green-400" : "bg-gray-400"}`}></span>
                            {m.status === "active" ? "活跃" : "未活跃"}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setViewMember(m)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-gray-50 hover:bg-red-50 text-gray-500 hover:text-red-600 rounded-xl text-xs cursor-pointer transition-colors whitespace-nowrap border border-gray-200 hover:border-red-200"
                          >
                            <i className="ri-eye-line"></i>查看
                          </button>
                          {!expired && (
                            <button
                              onClick={() => openEditLevel(m)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-600 rounded-xl text-xs cursor-pointer transition-colors whitespace-nowrap border border-amber-200"
                            >
                              <i className="ri-medal-line"></i>修改等级
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filtered.length === 0 && (
              <div className="py-12 text-center">
                <i className="ri-user-search-line text-3xl text-gray-200 mb-2 block"></i>
                <p className="text-sm text-gray-400">
                  {listTab === "expired" ? "暂无已过期会员" : "未找到匹配的会员"}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {viewMemberLive && (
        <MemberDetailModal
          tenantLegacyId={tenantLegacyId}
          member={viewMemberLive}
          onClose={() => setViewMember(null)}
          onDataChanged={() => void reload()}
        />
      )}

      {editingMember && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => e.target === e.currentTarget && setEditingMemberId(null)}
        >
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-amber-50 rounded-full flex items-center justify-center">
                  <i className="ri-medal-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">修改会员等级</h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {editingMember.name} · {editingMember.realName}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setEditingMemberId(null)}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-gray-500 text-sm"></i>
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                <span className="text-xs text-gray-500">当前等级</span>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${memberLevelStore.getClassName(getMemberLevel(editingMember))}`}>
                  {getMemberLevel(editingMember)}
                </span>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">选择新等级</p>
                <div className="space-y-1.5">
                  {levels.map((lv) => (
                    <button
                      key={lv.id}
                      onClick={() => setEditLevelValue(lv.name)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all cursor-pointer text-left ${
                        editLevelValue === lv.name ? "border-amber-400 bg-amber-50" : "border-gray-100 hover:border-gray-200 bg-white"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          editLevelValue === lv.name ? "border-amber-500 bg-amber-500" : "border-gray-300"
                        }`}
                      >
                        {editLevelValue === lv.name && <i className="ri-check-line text-white text-[10px]"></i>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${lv.className}`}>{lv.name}</span>
                          {lv.isSystem && <span className="text-[10px] text-gray-300">系统等级</span>}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 truncate">{lv.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">变更原因（可选）</label>
                <input
                  type="text"
                  value={editLevelNote}
                  onChange={(e) => setEditLevelNote(e.target.value)}
                  placeholder="如：会员续费升级、特别贡献奖励..."
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
                />
              </div>
            </div>

            <div className="flex gap-3 px-5 pb-5">
              <button
                onClick={() => setEditingMemberId(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={() => void confirmEditLevel()}
                disabled={levelSaving || !editLevelValue || editLevelValue === getMemberLevel(editingMember)}
                className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-medium cursor-pointer hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-1.5"
              >
                <i className="ri-check-line"></i>
                {levelSaving ? "保存中…" : "确认修改"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
